'use client';

import { PythonPipelineTrace } from './PythonPipelineTrace';
import type { TraceFile } from '@terra/shared-schemas-ai';
import { Box, HStack, css } from '@terra/ui-styled-system';
import {
  CheckmarkBadge01Icon,
  ArrowDown01Icon,
  ArrowRight01Icon,
  SourceCodeIcon,
  Globe02Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { TraceExpandChevron } from '../../chat/trace-view/components/TraceExpandChevron';
import {
  type KeyboardEvent,
  type MouseEvent,
  type CSSProperties,
  type ReactNode,
  useState,
  useEffect,
} from 'react';
import { Collapsible } from '../../../components/collapsible';
import { Text } from '../../../components/styled/text';
import { Tooltip } from '../../../components/tooltip';
import { HttpArtifact } from '../http/HttpArtifact';
import { useChatHarOptional } from '../../chat/har';
import { useArtifactsOptional } from '../../chat/trace-view/ArtifactsProvider';
import { useTraceOptional } from '../../chat/trace-view/TraceFilesProvider';
import { useTraceViewSettings } from '../../chat/trace-view/TraceViewSettingsContext';
import {
  type TracePhase,
  type TraceToolCall,
} from '../../chat/trace-view/types';
import { formatDurationBySeconds } from '../../chat/trace-view/utils';
import { normalizeToolName } from '../../chat/trace-view/utils/tool-config';
import { AddToEvidenceButton } from '../components/AddToEvidenceButton';
import {
  findRelatedTraceFiles,
  parseCodeResult,
} from '../../chat/trace-view/utils/trace-files';
import { shouldShowTraceToolShimmer } from '../../chat/trace-view/utils/trace-text-shimmer';
import {
  getTraceFileLanguage,
  getTraceFileType,
} from '../../chat/trace-view/utils/toolsUtils';
import {
  resolveCoderDisplayResult,
  extractCoderMarkdownResult,
} from '../../chat/trace-view/utils/coder-tool-output';
import { ReadOnlyCodeEditor } from '../../chat/components/ReadOnlyCodeEditor';

const pythonFileBlockContentClassName = css({
  '& .wmde-markdown': {
    padding: 0,
    margin: 0,
  },
  '& .wmde-markdown pre': {
    padding: 0,
    margin: 0,
  },
});

type PythonCodeTimelineData = {
  code: string;
  codeLanguage: string;
  result: string;
  resultLanguage: string;
  error?: string;
  hasError?: boolean;
  errorLabel?: string;
  isEvidence?: boolean;
  pythonRuntime?: number;
  headerActions?: ReactNode;
  body?: ReactNode;
  isResultPending?: boolean;
};

export type PythonExecutionCardProps = {
  code: string;
  codeLanguage?: 'python' | 'javascript' | 'json' | 'text';
  result: string;
  resultLanguage?: 'python' | 'javascript' | 'json' | 'text';
  error?: string;
  hasError?: boolean;
  errorLabel?: string;
  pythonRuntime?: number;
  headerActions?: ReactNode;
  body?: ReactNode;
  isResultPending?: boolean;
};
const pendingResultStyle: CSSProperties = {
  background:
    'linear-gradient(90deg, var(--colors-bg-muted) 25%, var(--colors-border-neutral-muted) 50%, var(--colors-bg-muted) 75%)',
  backgroundSize: '200% 100%',
};

type PythonExecutionSectionHeaderProps = {
  label: string;
  isExpanded: boolean;
  isToggleable: boolean;
  onToggle: () => void;
  onHideMessage?: () => void;
  trailing?: ReactNode;
};

function PythonExecutionSectionHeader({
  label,
  isExpanded,
  isToggleable,
  onToggle,
  onHideMessage,
  trailing,
}: PythonExecutionSectionHeaderProps) {
  return (
    <HStack
      px={2}
      h="28px"
      cursor={isToggleable ? 'pointer' : 'default'}
      onClick={isToggleable ? onToggle : undefined}
      width="100%"
      gap={1.5}
      alignItems="center"
      justify="space-between"
    >
      <HStack gap={1.5} alignItems="center">
        <Text fontSize="xs" fontWeight="medium" color="fg.muted">
          {label}
        </Text>
        {onHideMessage ? (
          <TraceExpandChevron
            isExpanded={isExpanded}
            size={10}
            tracePhase="co-pilot"
            onHideMessage={onHideMessage}
          />
        ) : (
          <Box display="flex" alignItems="center" color="fg.muted">
            {isExpanded ? (
              <ArrowDown01Icon size={10} />
            ) : (
              <ArrowRight01Icon size={10} />
            )}
          </Box>
        )}
      </HStack>
      {trailing}
    </HStack>
  );
}

function PythonExecutionBody({
  code,
  codeLanguage,
  result,
  resultLanguage,
  error,
  hasError,
  errorLabel,
  pythonRuntime,
  isResultPending,
  defaultExpanded = false,
  onHideMessage,
}: Pick<
  PythonCodeTimelineData,
  | 'code'
  | 'codeLanguage'
  | 'result'
  | 'resultLanguage'
  | 'error'
  | 'hasError'
  | 'errorLabel'
  | 'pythonRuntime'
  | 'isResultPending'
> & {
  defaultExpanded?: boolean;
  onHideMessage?: () => void;
}) {
  const t = useTranslations('designSystem.artifacts');
  const [expandedSections, setExpandedSections] = useState({
    script: defaultExpanded,
    result: defaultExpanded,
  });

  useEffect(() => {
    if (!defaultExpanded) {
      return;
    }

    setExpandedSections({ script: true, result: true });
  }, [defaultExpanded]);
  const hasCode = code.trim().length > 0;
  const codeResult = result;
  const hasResultOrErrorBody =
    codeResult.trim().length > 0 || Boolean(error?.trim());
  const hasResultMetadata = hasError === true || pythonRuntime !== undefined;
  const hasResultSection =
    hasResultOrErrorBody || hasResultMetadata || isResultPending === true;

  if (!hasCode && !hasResultSection) {
    return null;
  }

  const toggleSection = (section: 'script' | 'result') => {
    setExpandedSections((previousSections) => ({
      ...previousSections,
      [section]: !previousSections[section],
    }));
  };

  return (
    <>
      {hasCode && (
        <Box
          bg="transparent"
          borderBottomWidth={hasResultSection ? '1px' : '0'}
          borderBottomColor="border.neutral.muted"
          width="100%"
        >
          <PythonExecutionSectionHeader
            label={t('code')}
            isExpanded={expandedSections.script}
            isToggleable
            onToggle={() => toggleSection('script')}
            onHideMessage={onHideMessage}
          />

          {expandedSections.script && (
            <Box
              borderTopWidth="1px"
              borderColor="border.neutral.muted"
              bg="transparent"
              maxH="300px"
              overflow="hidden"
              className={pythonFileBlockContentClassName}
            >
              <ReadOnlyCodeEditor
                value={code}
                maxHeight="300px"
                minHeight="44px"
                showLineNumbers
                language={
                  codeLanguage === 'python' ||
                  codeLanguage === 'javascript' ||
                  codeLanguage === 'json'
                    ? codeLanguage
                    : 'python'
                }
              />
            </Box>
          )}
        </Box>
      )}

      {hasResultSection && (
        <Box bg="transparent" width="100%">
          {isResultPending ? (
            <HStack px={2} h="28px" alignItems="center">
              <Box
                h="1.5"
                w="120px"
                borderRadius="full"
                animationStyle="shimmer"
                style={pendingResultStyle}
              />
            </HStack>
          ) : (
            <>
              <PythonExecutionSectionHeader
                label={t('result')}
                isExpanded={expandedSections.result}
                isToggleable={hasResultOrErrorBody}
                onToggle={() => toggleSection('result')}
                onHideMessage={onHideMessage}
                trailing={
                  <HStack gap={1.5} alignItems="center">
                    {hasError && (
                      <Text fontSize="xs" fontWeight="normal" color="fg.subtle">
                        {errorLabel ?? t('canceled')}
                      </Text>
                    )}
                    {pythonRuntime !== undefined && (
                      <Text fontSize="xs" fontFamily="mono" color="fg.muted">
                        {formatDurationBySeconds(pythonRuntime)}
                      </Text>
                    )}
                  </HStack>
                }
              />

              {hasResultOrErrorBody && expandedSections.result && (
                <Box
                  borderTopWidth="1px"
                  borderColor="border.neutral.muted"
                  bg="transparent"
                  maxH="300px"
                  overflow={codeResult.trim().length > 0 ? 'hidden' : 'auto'}
                  className={pythonFileBlockContentClassName}
                >
                  {codeResult.trim().length > 0 ? (
                    <ReadOnlyCodeEditor
                      value={codeResult}
                      maxHeight="300px"
                      minHeight="44px"
                      showLineNumbers
                      language={
                        resultLanguage === 'python' ||
                        resultLanguage === 'javascript' ||
                        resultLanguage === 'json'
                          ? resultLanguage
                          : 'text'
                      }
                    />
                  ) : (
                    <Box px={3} py={2}>
                      <Text
                        fontSize="xs"
                        color="fg.default"
                        whiteSpace="pre-wrap"
                      >
                        {error}
                      </Text>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      )}
    </>
  );
}

function PythonCodeTimeline({
  code,
  codeLanguage,
  result,
  resultLanguage,
  error,
  hasError,
  errorLabel,
  isEvidence,
  pythonRuntime,
  headerActions,
  body,
  isResultPending,
}: PythonCodeTimelineData) {
  const t = useTranslations('designSystem.artifacts');
  const tEvidence = useTranslations('designSystem.artifacts.evidenceBadge');
  const hasCode = code.trim().length > 0;
  const codeResult = result;
  const hasResultOrErrorBody =
    codeResult.trim().length > 0 || Boolean(error?.trim());
  const hasTimelineChrome =
    hasCode ||
    hasResultOrErrorBody ||
    isResultPending === true ||
    hasError === true ||
    pythonRuntime !== undefined ||
    Boolean(headerActions) ||
    Boolean(body);

  if (!hasTimelineChrome) {
    return null;
  }

  return (
    <Box
      borderWidth="1px"
      borderColor="border.neutral.muted"
      borderRadius="md"
      bg="transparent"
      overflow="hidden"
      width="100%"
    >
      <HStack
        justify="space-between"
        alignItems="center"
        px={2}
        h="28px"
        bg="transparent"
        borderBottomWidth="1px"
        borderBottomColor="border.neutral.muted"
      >
        <HStack gap={1.5} alignItems="center">
          <Text fontSize="xs" fontWeight="medium" color="fg.default">
            {t('pythonExecution')}
          </Text>
        </HStack>
        {(isEvidence || headerActions) && (
          <HStack gap={1} alignItems="center">
            {isEvidence && (
              <Tooltip title={tEvidence('markedAsEvidence')}>
                <Box color="green.9" display="flex" alignItems="center">
                  <CheckmarkBadge01Icon size={12} />
                </Box>
              </Tooltip>
            )}
            {headerActions}
          </HStack>
        )}
      </HStack>
      {body ?? (
        <PythonExecutionBody
          code={code}
          codeLanguage={codeLanguage}
          result={result}
          resultLanguage={resultLanguage}
          error={error}
          hasError={hasError}
          errorLabel={errorLabel}
          pythonRuntime={pythonRuntime}
          isResultPending={isResultPending}
        />
      )}
    </Box>
  );
}

export function PythonExecutionCard({
  code,
  codeLanguage = 'python',
  result,
  resultLanguage = 'text',
  error,
  hasError,
  errorLabel,
  pythonRuntime,
  headerActions,
  body,
  isResultPending,
}: PythonExecutionCardProps) {
  return (
    <PythonCodeTimeline
      code={code}
      codeLanguage={codeLanguage}
      result={result}
      resultLanguage={resultLanguage}
      error={error}
      hasError={hasError}
      errorLabel={errorLabel}
      pythonRuntime={pythonRuntime}
      headerActions={headerActions}
      body={body}
      isResultPending={isResultPending}
    />
  );
}

type ResolvedPythonData = {
  code: string;
  codeLanguage: string;
  result: string;
  resultLanguage: string;
  pythonRuntime?: number;
  hasError: boolean;
  hasResult: boolean;
  isFailedToolCall: boolean;
  failureMessage: string;
  errorLabel?: string;
  blockedCode: string;
  shouldShowFailureMessageOnly: boolean;
};
type PythonArtifactProps = {
  toolCall: TraceToolCall;
  displayName: string;
  tracePhase?: TracePhase;
  onHideMessage?: () => void;
};
type PythonArtifactGroupProps = {
  toolCalls: TraceToolCall[];
  tracePhase?: TracePhase;
  onHideMessage?: () => void;
};
type PythonArtifactRunRowProps = {
  toolCall: TraceToolCall;
  runLabel: string;
  tracePhase?: TracePhase;
  onHideMessage?: () => void;
};
type PythonArtifactActionsProps = {
  artifactId: string | undefined;
  isEvidence?: boolean;
  showHttpView: boolean;
  onShowCode: () => void;
  onShowHttp: () => void;
  showHttpToggle: boolean;
};
const SAFE_CODER_ERROR_MESSAGE = 'An error occurred during python execution.';

type CoderStreamOutput = {
  script?: string;
  code?: string;
  output?:
    | {
        result?: unknown;
        stdout?: unknown[];
        codeRuntime?: number;
        success?: boolean;
      }
    | string;
  result?: unknown;
};

function readCoderInput(args: Record<string, unknown> | undefined): {
  script?: string;
  code?: string;
} {
  if (!args) {
    return {};
  }

  return {
    script: typeof args.script === 'string' ? args.script : undefined,
    code: typeof args.code === 'string' ? args.code : undefined,
  };
}

function isCoderExecutionStatusMessage(value: string): boolean {
  return /^Python script execution (completed|failed)/i.test(value.trim());
}

function parseCoderStructuredOutput(
  output: string | unknown,
  message?: string,
): {
  code: string;
  result: string;
  runtime?: number;
} | null {
  let record: unknown = output;

  if (typeof output === 'string') {
    const trimmed = output.trim();

    if (!trimmed) {
      return null;
    }

    try {
      record = JSON.parse(trimmed);
    } catch {
      if (isCoderExecutionStatusMessage(trimmed)) {
        return null;
      }

      return { code: '', result: trimmed, runtime: undefined };
    }
  }

  if (typeof record !== 'object' || record === null) {
    return null;
  }

  const parsed = record as CoderStreamOutput;
  const inputFromRecord = readCoderInput(parsed as Record<string, unknown>);
  const code =
    typeof parsed.script === 'string'
      ? parsed.script
      : typeof parsed.code === 'string'
        ? parsed.code
        : inputFromRecord.script || inputFromRecord.code || '';
  const nestedOutput = parsed.output;
  const result =
    resolveCoderDisplayResult({
      output: record,
      message,
    }) ?? '';
  let runtime: number | undefined;

  if (typeof nestedOutput === 'object' && nestedOutput !== null) {
    runtime = nestedOutput.codeRuntime;
  } else if (typeof nestedOutput === 'string') {
    const trimmedNestedOutput = nestedOutput.trim();

    if (
      trimmedNestedOutput.startsWith('{') ||
      trimmedNestedOutput.startsWith('[')
    ) {
      try {
        const parsedNestedOutput = JSON.parse(trimmedNestedOutput) as {
          codeRuntime?: number;
        };

        runtime = parsedNestedOutput.codeRuntime;
      } catch {
        // ignore malformed nested runtime payload
      }
    }
  }

  if (!code && !result) {
    return null;
  }

  return { code, result, runtime };
}

function parseCoderOutputFromToolCall(toolCall: TraceToolCall): {
  code: string;
  result: string;
  runtime?: number;
} | null {
  const fromOutput = toolCall.output
    ? parseCoderStructuredOutput(toolCall.output, toolCall.streamMessage)
    : null;
  const input = readCoderInput(toolCall.args);
  const code = fromOutput?.code || input.script || input.code || '';
  const result =
    fromOutput?.result ||
    resolveCoderDisplayResult({
      output: toolCall.output,
      message: toolCall.streamMessage,
    }) ||
    (typeof toolCall.output === 'string'
      ? extractCoderMarkdownResult(toolCall.output)
      : '') ||
    '';
  const runtime = fromOutput?.runtime;

  if (!code && !result) {
    return null;
  }

  return { code, result, runtime };
}

function PythonArtifactActions({
  artifactId,
  isEvidence,
  showHttpView,
  onShowCode,
  onShowHttp,
  showHttpToggle,
}: PythonArtifactActionsProps) {
  const t = useTranslations('designSystem.artifacts');

  return (
    <HStack gap={1} alignItems="center">
      <AddToEvidenceButton
        artifactId={artifactId}
        label="Add as evidence"
        isEvidence={isEvidence}
      />
      {showHttpToggle && (
        <HStack
          gap="0"
          borderWidth="1px"
          borderColor="border.neutral.muted"
          borderRadius="sm"
          overflow="hidden"
          flexShrink={0}
        >
          <Tooltip title={t('showCode')}>
            <Box
              as="button"
              aria-label={t('showCode')}
              onClick={onShowCode}
              h="5"
              w="5"
              display="flex"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              bg={!showHttpView ? 'bg.muted' : 'transparent'}
              color={!showHttpView ? 'fg.default' : 'fg.muted'}
              _hover={{
                bg: !showHttpView ? 'bg.muted' : 'bg.hover',
              }}
            >
              <SourceCodeIcon size={10} />
            </Box>
          </Tooltip>
          <Tooltip title={t('showHttpRequests')}>
            <Box
              as="button"
              aria-label={t('showHttpRequests')}
              onClick={onShowHttp}
              h="5"
              w="5"
              display="flex"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              bg={showHttpView ? 'bg.muted' : 'transparent'}
              color={showHttpView ? 'fg.default' : 'fg.muted'}
              _hover={{
                bg: showHttpView ? 'bg.muted' : 'bg.hover',
              }}
            >
              <Globe02Icon size={10} />
            </Box>
          </Tooltip>
        </HStack>
      )}
    </HStack>
  );
}

function resolvePythonArtifactData(
  toolCall: TraceToolCall,
  artifact:
    | {
        actionContent?: string | null;
        resultContent?: string | null;
        durationMs?: number | null;
      }
    | undefined,
  relatedFiles: {
    codeFile?: TraceFile;
    resultFile?: TraceFile;
  },
): ResolvedPythonData {
  const normalizedName = normalizeToolName(toolCall.name);
  const isCoderTool =
    normalizedName === 'coder' ||
    normalizedName === 'sub-agent-coder' ||
    normalizedName === 'coder-tool' ||
    normalizedName === 'python' ||
    normalizedName === 'python-tool';
  const trimmedOutput = toolCall.output?.trim() ?? '';
  const coderFromOutput = isCoderTool
    ? (parseCoderOutputFromToolCall(toolCall) ??
      (trimmedOutput ? parseCoderStructuredOutput(trimmedOutput) : null))
    : null;
  const argsInput = readCoderInput(toolCall.args);
  const rawCode =
    artifact?.actionContent ??
    relatedFiles.codeFile?.content ??
    coderFromOutput?.code ??
    argsInput.script ??
    argsInput.code ??
    '';
  const rawResult =
    artifact?.resultContent ??
    (relatedFiles.resultFile
      ? relatedFiles.resultFile.name.includes('code_result_')
        ? parseCodeResult(relatedFiles.resultFile.content)
        : relatedFiles.resultFile.content
      : undefined) ??
    coderFromOutput?.result ??
    resolveCoderDisplayResult({
      output: toolCall.output,
      message: toolCall.streamMessage,
    }) ??
    '';
  const codeFileType = relatedFiles.codeFile
    ? getTraceFileType(
        relatedFiles.codeFile.name,
        relatedFiles.codeFile.content,
      )
    : 'text';
  const codeLanguage = relatedFiles.codeFile
    ? getTraceFileLanguage(rawCode, codeFileType)
    : 'python';
  const resultFileType = relatedFiles.resultFile
    ? getTraceFileType(
        relatedFiles.resultFile.name,
        relatedFiles.resultFile.content,
      )
    : 'text';
  const resultLanguage = relatedFiles.resultFile
    ? getTraceFileLanguage(rawResult, resultFileType)
    : 'text';
  const pythonRuntime =
    artifact?.durationMs ??
    coderFromOutput?.runtime ??
    toolCall.timing?.durationMs ??
    undefined;
  const isFailedToolCall = toolCall.status === 'error';
  const hasError = isFailedToolCall || Boolean(toolCall.error);
  const errorLabel =
    typeof toolCall.metadata?.errorLabel === 'string' &&
    toolCall.metadata.errorLabel.trim().length > 0
      ? toolCall.metadata.errorLabel
      : undefined;
  const blockedCode =
    typeof toolCall.metadata?.codeBlocked === 'string'
      ? toolCall.metadata.codeBlocked.trim()
      : '';
  const trimmedCode = rawCode.trim();
  const trimmedResult = rawResult.trim();
  const trimmedToolError = toolCall.error?.trim() ?? '';
  const hasResult =
    trimmedCode.length > 0 ||
    trimmedResult.length > 0 ||
    trimmedToolError.length > 0 ||
    isFailedToolCall ||
    pythonRuntime !== undefined ||
    (isCoderTool && toolCall.status === 'completed');

  return {
    code: rawCode,
    codeLanguage,
    result: rawResult,
    resultLanguage,
    pythonRuntime,
    hasError,
    hasResult,
    isFailedToolCall,
    failureMessage: toolCall.error || SAFE_CODER_ERROR_MESSAGE,
    errorLabel,
    blockedCode,
    shouldShowFailureMessageOnly: isCoderTool && isFailedToolCall,
  };
}

function useResolvedPythonArtifact(
  toolCall: TraceToolCall,
  tracePhase: TracePhase = 'attack',
) {
  const artifactsContext = useArtifactsOptional();
  const traceContext = useTraceOptional();
  const chatHar = useChatHarOptional();
  const { shouldShowHttpView, setCardHttpView } = useTraceViewSettings();
  const showHttpToggle = chatHar?.hasHarPresence?.(toolCall.id) ?? false;
  const showHttpView =
    tracePhase !== 'co-pilot' &&
    showHttpToggle &&
    shouldShowHttpView(toolCall.id);
  const artifact = artifactsContext?.getArtifactByToolCallId(toolCall.id);
  const relatedFiles = artifact
    ? { codeFile: undefined, resultFile: undefined }
    : findRelatedTraceFiles(
        toolCall,
        traceContext?.findTraceFiles,
        traceContext?.getTraceFile,
        traceContext?.traceFiles,
        0,
      );
  const data = resolvePythonArtifactData(toolCall, artifact, relatedFiles);
  const artifactId = artifact?.id ?? artifact?.runtimeId ?? undefined;
  const isEvidence = artifactsContext?.isEvidence(artifact) ?? false;
  const isCompleted = toolCall.status === 'completed';

  const headerActions = isCompleted ? (
    <PythonArtifactActions
      artifactId={artifactId}
      isEvidence={isEvidence}
      showHttpView={showHttpView}
      onShowCode={() => setCardHttpView(toolCall.id, false)}
      onShowHttp={() => setCardHttpView(toolCall.id, true)}
      showHttpToggle={showHttpToggle}
    />
  ) : null;

  return {
    data,
    artifact,
    artifactId,
    isCompleted,
    isEvidence,
    showHttpView,
    headerActions,
  };
}

function PythonArtifactExpandedContent({
  toolCall,
  data,
  isCompleted,
  showHttpView,
  tracePhase,
  onHideMessage,
}: {
  toolCall: TraceToolCall;
  data: ResolvedPythonData;
  isCompleted: boolean;
  showHttpView: boolean;
  tracePhase?: TracePhase;
  onHideMessage?: () => void;
}) {
  const defaultExpanded = false;

  if (showHttpView && isCompleted) {
    return (
      <HttpArtifact
        toolCallId={toolCall.id}
        toolType="PYTHON"
        showSummaryHeader={true}
        embedded
      />
    );
  }

  if (data.shouldShowFailureMessageOnly) {
    return (
      <PythonExecutionBody
        code={data.blockedCode || data.code}
        codeLanguage={data.codeLanguage}
        result=""
        resultLanguage="text"
        error={data.failureMessage}
        hasError={data.hasError}
        errorLabel={data.errorLabel}
        pythonRuntime={data.pythonRuntime}
        defaultExpanded={defaultExpanded}
        onHideMessage={tracePhase === 'co-pilot' ? onHideMessage : undefined}
      />
    );
  }

  return (
    <PythonExecutionBody
      code={data.code}
      codeLanguage={data.codeLanguage}
      result={data.result}
      resultLanguage={data.resultLanguage}
      error={toolCall.error}
      hasError={data.hasError}
      errorLabel={data.errorLabel}
      pythonRuntime={data.pythonRuntime}
      defaultExpanded={defaultExpanded}
      onHideMessage={tracePhase === 'co-pilot' ? onHideMessage : undefined}
    />
  );
}

function PythonArtifactRunRow({
  toolCall,
  runLabel,
  tracePhase,
  onHideMessage,
}: PythonArtifactRunRowProps) {
  const t = useTranslations('designSystem.artifacts');
  const { data, isCompleted, showHttpView, headerActions } =
    useResolvedPythonArtifact(toolCall, tracePhase);
  const { hasError, hasResult } = data;
  const hasExpandedContent =
    (showHttpView && isCompleted) ||
    data.code.trim().length > 0 ||
    data.result.trim().length > 0 ||
    Boolean(toolCall.error?.trim()) ||
    (data.shouldShowFailureMessageOnly &&
      (data.blockedCode.trim().length > 0 ||
        data.failureMessage.trim().length > 0));
  const [isOpen, setIsOpen] = useState(
    tracePhase === 'co-pilot' && hasExpandedContent,
  );
  const showShimmer = shouldShowTraceToolShimmer({
    hasResult,
    status: toolCall.status,
  });
  const shimmerStyle = showShimmer
    ? {
        background:
          'linear-gradient(90deg, var(--colors-fg-muted) 25%, var(--colors-fg-subtle) 50%, var(--colors-fg-muted) 75%)',
        backgroundSize: '200% 100%',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        animationTimingFunction: 'linear',
      }
    : undefined;

  return (
    <Collapsible.Root
      open={isOpen}
      onOpenChange={({ open }: { open: boolean }) => setIsOpen(open)}
      lazyMount
      unmountOnExit
      w="full"
      width="100%"
    >
      <Box borderTopWidth="1px" borderTopColor="border.neutral.muted" w="full">
        <HStack
          h="28px"
          px={2}
          justify="space-between"
          alignItems="center"
          cursor={hasExpandedContent ? 'pointer' : 'default'}
          bg="transparent"
          role={hasExpandedContent ? 'button' : undefined}
          tabIndex={hasExpandedContent ? 0 : undefined}
          _hover={hasExpandedContent ? { bg: 'bg.hover' } : undefined}
          onClick={
            hasExpandedContent
              ? () => setIsOpen((previous) => !previous)
              : undefined
          }
          onKeyDown={
            hasExpandedContent
              ? (event: KeyboardEvent<HTMLDivElement>) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setIsOpen((previous) => !previous);
                  }
                }
              : undefined
          }
        >
          <HStack gap={1.5} alignItems="center" minW={0}>
            {tracePhase === 'co-pilot' && onHideMessage ? (
              <TraceExpandChevron
                isExpanded={isOpen}
                size={10}
                tracePhase={tracePhase}
                onHideMessage={onHideMessage}
              />
            ) : (
              <Box
                display="flex"
                alignItems="center"
                color="fg.muted"
                opacity={hasExpandedContent ? 1 : 0}
              >
                {isOpen ? (
                  <ArrowDown01Icon size={10} />
                ) : (
                  <ArrowRight01Icon size={10} />
                )}
              </Box>
            )}
            <Text
              fontSize="xs"
              fontWeight="medium"
              color="fg.default"
              animationStyle={showShimmer ? 'shimmer' : undefined}
              style={shimmerStyle}
            >
              {runLabel}
            </Text>
          </HStack>
          {(hasError || headerActions) && (
            <HStack
              gap={1}
              alignItems="center"
              flexShrink={0}
              onClick={(event: MouseEvent<HTMLDivElement>) =>
                event.stopPropagation()
              }
            >
              {hasError && (
                <Text fontSize="xs" fontWeight="normal" color="fg.subtle">
                  {data.errorLabel ?? t('canceled')}
                </Text>
              )}
              {headerActions}
            </HStack>
          )}
        </HStack>
        {hasExpandedContent && (
          <Collapsible.Content w="full" width="100%">
            <Box
              bg="transparent"
              borderTopWidth="1px"
              borderColor="border.neutral.muted"
              w="full"
              width="100%"
            >
              <PythonArtifactExpandedContent
                toolCall={toolCall}
                data={data}
                isCompleted={isCompleted}
                showHttpView={showHttpView}
                tracePhase={tracePhase}
                onHideMessage={onHideMessage}
              />
            </Box>
          </Collapsible.Content>
        )}
      </Box>
    </Collapsible.Root>
  );
}

export function PythonArtifactGroup({
  toolCalls,
  tracePhase,
  onHideMessage,
}: PythonArtifactGroupProps) {
  const t = useTranslations('designSystem.artifacts');
  const artifactsContext = useArtifactsOptional();

  if (toolCalls.length === 0) {
    return null;
  }

  if (toolCalls.length === 1 && toolCalls[0]) {
    return (
      <PythonArtifact
        toolCall={toolCalls[0]}
        displayName={t('pythonExecution')}
        tracePhase={tracePhase}
        onHideMessage={onHideMessage}
      />
    );
  }

  const runtimes = toolCalls
    .map((toolCall) => {
      const artifact = artifactsContext?.getArtifactByToolCallId(toolCall.id);

      return artifact?.durationMs ?? undefined;
    })
    .filter((runtime): runtime is number => runtime !== undefined);
  const totalRuntime =
    runtimes.length > 0
      ? runtimes.reduce((total, runtime) => total + runtime, 0)
      : undefined;

  return (
    <Box
      borderWidth="1px"
      borderColor="border.neutral.muted"
      borderRadius="md"
      bg="transparent"
      overflow="hidden"
      width="100%"
    >
      <HStack
        justify="space-between"
        alignItems="center"
        px={2}
        h="28px"
        bg="transparent"
      >
        <Text fontSize="xs" fontWeight="medium" color="fg.default">
          {t('pythonExecutions')}
        </Text>
        <HStack gap={1.5} alignItems="center">
          <Text fontSize="xs" color="fg.muted">
            {t('runCount', { count: toolCalls.length })}
          </Text>
          {totalRuntime !== undefined && (
            <Text fontSize="xs" fontFamily="mono" color="fg.muted">
              {formatDurationBySeconds(totalRuntime)}
            </Text>
          )}
        </HStack>
      </HStack>
      {toolCalls.map((toolCall, index) => (
        <PythonArtifactRunRow
          key={toolCall.id}
          toolCall={toolCall}
          runLabel={t('runLabel', { index: index + 1 })}
          tracePhase={tracePhase}
          onHideMessage={onHideMessage}
        />
      ))}
    </Box>
  );
}

export function PythonArtifact(props: PythonArtifactProps) {
  if (normalizeToolName(props.toolCall.name) === 'python') {
    return (
      <PythonPipelineTrace toolCall={props.toolCall}>
        <PythonArtifactContent {...props} />
      </PythonPipelineTrace>
    );
  }

  return <PythonArtifactContent {...props} />;
}

function PythonArtifactContent({
  toolCall,
  displayName,
  tracePhase,
  onHideMessage,
}: PythonArtifactProps) {
  const { data, isCompleted, isEvidence, showHttpView, headerActions } =
    useResolvedPythonArtifact(toolCall, tracePhase);
  const {
    code,
    codeLanguage,
    result,
    resultLanguage,
    pythonRuntime,
    hasError,
    hasResult,
    failureMessage,
    errorLabel,
    blockedCode,
    shouldShowFailureMessageOnly,
  } = data;
  const defaultExpanded = false;
  const showShimmer = shouldShowTraceToolShimmer({
    hasResult,
    status: toolCall.status,
  });
  const shimmerStyle = showShimmer
    ? {
        background:
          'linear-gradient(90deg, var(--colors-fg-muted) 25%, var(--colors-fg-subtle) 50%, var(--colors-fg-muted) 75%)',
        backgroundSize: '200% 100%',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        animationTimingFunction: 'linear',
      }
    : undefined;
  const toolNameText = (
    <Text
      fontSize="xs"
      color="fg.muted"
      animationStyle={showShimmer ? 'shimmer' : undefined}
      style={shimmerStyle}
    >
      {displayName}
    </Text>
  );

  if (!hasResult) {
    return (
      <HStack w="full" gap={1.5} alignItems="center" py={1} borderRadius="sm">
        {toolNameText}
      </HStack>
    );
  }

  if (showHttpView && isCompleted) {
    return (
      <PythonCodeTimeline
        code=""
        codeLanguage="python"
        result=""
        resultLanguage="text"
        hasError={hasError}
        isEvidence={isEvidence}
        pythonRuntime={pythonRuntime}
        headerActions={headerActions}
        body={
          <HttpArtifact
            toolCallId={toolCall.id}
            toolType="PYTHON"
            showSummaryHeader={true}
            embedded
          />
        }
      />
    );
  }

  if (shouldShowFailureMessageOnly) {
    return (
      <PythonCodeTimeline
        code={blockedCode || data.code}
        codeLanguage={data.codeLanguage}
        result=""
        resultLanguage="text"
        error={failureMessage}
        hasError={true}
        isEvidence={isEvidence}
        pythonRuntime={pythonRuntime}
        headerActions={headerActions}
        body={
          <PythonExecutionBody
            code={blockedCode || data.code}
            codeLanguage={data.codeLanguage}
            result=""
            resultLanguage="text"
            error={failureMessage}
            hasError={true}
            errorLabel={errorLabel}
            pythonRuntime={pythonRuntime}
            defaultExpanded={defaultExpanded}
            onHideMessage={
              tracePhase === 'co-pilot' ? onHideMessage : undefined
            }
          />
        }
      />
    );
  }

  return (
    <PythonCodeTimeline
      code={code}
      codeLanguage={codeLanguage}
      result={result}
      resultLanguage={resultLanguage}
      error={toolCall.error}
      hasError={hasError}
      isEvidence={isEvidence}
      pythonRuntime={pythonRuntime}
      headerActions={headerActions}
      body={
        <PythonExecutionBody
          code={code}
          codeLanguage={codeLanguage}
          result={result}
          resultLanguage={resultLanguage}
          error={toolCall.error}
          hasError={hasError}
          errorLabel={errorLabel}
          pythonRuntime={pythonRuntime}
          defaultExpanded={defaultExpanded}
          onHideMessage={tracePhase === 'co-pilot' ? onHideMessage : undefined}
        />
      }
    />
  );
}
